import React, { useMemo } from 'react'
import { css, Global } from '@emotion/core'
import { useFeatureFlag } from '@fs/zion-flags'
import PageAssembler from '../components/PageAssembler/PageAssembler'
import ChineseSurnamePage from './ChineseSurnamePage/ChineseSurnamePage'
import { useMemoryBook } from '../providers/MemoryBookProvider'
import { newPageState } from '../providers/MemoryBooksReducer'
import TreeDesignsPage from './TreeDesignsPage/TreeDesignsPage'
import FrontCoverPage from './FrontCoverPage/FrontCoverPage'
import BackPage from './BackPage/BackPage'
import GlobalSurnamePage from './GlobalSurnamePage/GlobalSurnamePage'
import PlaceholderSurnamePage from './PlaceholderSurnamePage/PlaceholderSurnamePage'
import useIsSafari from '../hooks/useIsSafari/useIsSafari'
import {
  FRONT_COVER_PAGE_IDS,
  BACK_COVER_PAGE_ID,
  SURNAME_PAGE_ID as CHINESE_SURNAME_PAGE_ID,
  GLOBAL_SURNAME_PAGE_ID,
  TREE_DESIGNS_PAGE_ID,
} from '../constants/templateIds'
import PrintStickerLayer from '../components/StickerLayer/PrintStickerLayer'
import { BACKGROUND_OPTIONS_BY_ID } from '../constants/backgroundOptions'

const PX_PER_INCH = 96
const PAGE_WIDTH_PX = 536
const PAGE_HEIGHT_PX = 758
const PAGE_PADDING_PX = 50

const PAGE_WIDTH_IN = PAGE_WIDTH_PX / PX_PER_INCH
const PAGE_HEIGHT_IN = PAGE_HEIGHT_PX / PX_PER_INCH
const PAGE_PADDING_IN = PAGE_PADDING_PX / PX_PER_INCH

const pageCSS = css`
  box-sizing: border-box;
  width: ${PAGE_WIDTH_PX}px;
  height: ${PAGE_HEIGHT_PX}px;
  padding: ${PAGE_PADDING_PX}px;
  margin: 1px auto;
  background: white;
  position: relative;
  overflow: hidden;

  @media print {
    width: ${PAGE_WIDTH_IN}in;
    height: ${PAGE_HEIGHT_IN}in;
    padding: ${PAGE_PADDING_IN}in;
    margin: 0 auto;
  }
`

const pageContainerCSS = css`
  display: block;
  background-color: aliceblue;
`

const globalPrintStyles = css`
  @media all {
    .page-break {
      display: none;
    }
  }

  @media print {
    html,
    body {
      height: initial !important;
      overflow: initial !important;
      margin: 0;
      padding: 0;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      font-size: 12pt;
    }

    * {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .page-break {
      display: block;
      height: 0;
      margin: 0;
      padding: 0;
      page-break-before: always;
      break-before: page;
    }
  }

  @page {
    size: ${PAGE_WIDTH_IN}in ${PAGE_HEIGHT_IN}in;
    margin: 0;
  }
`

const safariGatedStyles = css`
  /* These styles will *not* apply in Safari */
  @media print and (orientation: landscape) {
    width: 10in !important;
    height: 7in !important;
  }
`
const safariGatedGlobalStyles = css`
  /* These styles will *not* apply in Safari */
  @media print and (orientation: landscape) {
    html,
    body {
      font-size: 14pt !important;
    }
  }
`

const getBackgroundImageSrc = (backgroundId) => {
  if (backgroundId === undefined || backgroundId === null) {
    return null
  }

  const mappedOption = BACKGROUND_OPTIONS_BY_ID[backgroundId]
  if (mappedOption) {
    return mappedOption.printSrc
  }

  return null
}

function Page({ assemblyInstructions, templateId, pageNumber, stickers, backgroundId }) {
  const isSafari = useIsSafari()
  const isNewPrintFlag = useFeatureFlag('chinese_memoryBooks_newPrint').isOn

  const backgroundImageSrc = getBackgroundImageSrc(backgroundId)

  const pageCSStyles = useMemo(() => {
    const styles = isNewPrintFlag ? [] : [pageCSS, !isSafari && safariGatedStyles]

    if (backgroundImageSrc) {
      styles.push(css`
        background-image: url('${backgroundImageSrc}');
        background-size: cover;
        background-repeat: no-repeat;
        background-position: center;
      `)
    } else if (styles.length === 0) {
      styles.push(css`
        background: white;
      `)
    }

    return styles
  }, [isNewPrintFlag, isSafari, backgroundImageSrc])

  let content = null

  if (templateId === GLOBAL_SURNAME_PAGE_ID) {
    const globalSurnameProps = assemblyInstructions[0]?.props || {}
    content = (
      <GlobalSurnamePage
        surname={globalSurnameProps?.surnameId}
        surnamePage={globalSurnameProps?.surnamePage}
        showMap={globalSurnameProps?.surnamePage === '1'}
        pageNumber={pageNumber}
        isPrintView
      />
    )
  } else if (templateId === TREE_DESIGNS_PAGE_ID) {
    content = (
      <TreeDesignsPage pageNumber={pageNumber} memoryId={assemblyInstructions[0]?.props?.memoryId} isPrintView />
    )
  } else if (templateId === CHINESE_SURNAME_PAGE_ID) {
    const surnameId = assemblyInstructions[0]?.props?.surnameId
    if (!surnameId) {
      content = <PlaceholderSurnamePage isGlobalSurnamePage={false} />
    } else {
      content = (
        <ChineseSurnamePage
          surnameId={assemblyInstructions[0]?.props?.surnameId}
          color={assemblyInstructions[0]?.props?.color}
          isPrintView
          pageNumber={pageNumber}
        />
      )
    }
  } else if (FRONT_COVER_PAGE_IDS.includes(templateId)) {
    content = (
      <FrontCoverPage pageNumber={pageNumber} elements={assemblyInstructions[0]} templateId={templateId} isPrintView />
    )
  } else if (templateId === BACK_COVER_PAGE_ID) {
    content = <BackPage pageNumber={pageNumber} elements={assemblyInstructions[0]} isPrintView />
  } else {
    content = <PageAssembler assemblyInstructions={assemblyInstructions} pageNumber={pageNumber} isPrintView />
  }

  return (
    <div css={pageCSStyles}>
      {content}
      <PrintStickerLayer stickers={stickers} />
    </div>
  )
}
export default function PrintingPages({ includeBlankInnerCovers = false }) {
  const { isNewPrintFlag } = useFeatureFlag('chinese_memoryBooks_newPrint').isOn

  const { memoryBook } = useMemoryBook()
  const isSafari = useIsSafari()
  const contentPages = useMemo(() => memoryBook?.contentPages || [], [memoryBook])

  // Memoize the rearranged pages for print
  const printOrderPages = useMemo(() => {
    if (!contentPages.length) return []

    const filteredPages = contentPages.filter((page) => page.page !== 'back')
    const backPage = contentPages.find((page) => page.page === 'back') || null
    const frontPageIndex = filteredPages.findIndex((page) => page.page === 'front')

    const arrangedPages = [...filteredPages]

    if (frontPageIndex !== -1 && includeBlankInnerCovers) {
      const innerFrontPage = newPageState('inner-front')
      arrangedPages.splice(frontPageIndex + 1, 0, innerFrontPage)
    }

    if (includeBlankInnerCovers) {
      const innerBackPage = newPageState('inner-back')
      return backPage ? [...arrangedPages, innerBackPage, backPage] : arrangedPages
    }

    return backPage ? [...arrangedPages, backPage] : arrangedPages
  }, [contentPages, includeBlankInnerCovers])

  return (
    <>
      {!isNewPrintFlag && <Global styles={[globalPrintStyles, !isSafari && safariGatedGlobalStyles]} />}

      <div css={!isNewPrintFlag && pageContainerCSS}>
        {printOrderPages.map((page, index) => (
          <div key={page.id || `${page.page}-${index}`} className="pdf-page">
            <Page
              templateId={page?.templateId}
              assemblyInstructions={page?.elements}
              pageNumber={page.page}
              stickers={page.stickers}
              backgroundId={page.backgroundId}
            />

            {index < printOrderPages.length - 1 && !isNewPrintFlag && <div className="page-break" />}
          </div>
        ))}
      </div>
    </>
  )
}
